import { Injectable, Injector } from '@angular/core';
import { clamp, times } from '@sama/micro-dash';
import { DebouncedEventTracker } from '../../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { buildTrackerFromShellConnector } from '../../../../cross-team-shared/event-tracking/event-utils';
import { newDataForImage } from '../../../../cross-team-shared/state/image-from-data-state';
import { cacheImageSourcePixels } from '../../../../cross-team-shared/state/sensor-renderer-state';
import { LoadImageService } from '../../../../shared/misc-services/load-image.service';
import { TestHookService } from '../../../../shared/misc-services/test-hook.service';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { toMaskPosition } from '../../utils/mask-utils';
import { AbstractRasterCodingTool } from '../abstract-raster-coding-tool';
import { AbstractAlgorithmAdapter } from './algorithms/abstract-algorithm-adapter';
import { PFFAdapter } from './algorithms/pff-adapter';
import { SLICAdapter } from './algorithms/slic-adapter';
import { SLICOAdapter } from './algorithms/slico-adapter';
import { SuperpixelAlgorithmResult } from './js-segment-annotator-code/superpixel-algorithm-result';
import { SuperpixelDragHandler } from './superpixel-drag-handler';
import { SuperpixelSecondaryToolbarComponent } from './superpixel-secondary-toolbar.component';

export const computeSuperpixelHook = Symbol();

@Injectable()
export class SuperpixelTool extends AbstractRasterCodingTool {
  readonly name = 'Superpixel';
  override readonly secondaryToolbarType = SuperpixelSecondaryToolbarComponent;

  algorithms!: Array<AbstractAlgorithmAdapter<any>>;

  private sizeEventTracker: DebouncedEventTracker;
  private result!: SuperpixelAlgorithmResult;
  private positionLookup!: number[][];
  private _selectedAlgorithm!: AbstractAlgorithmAdapter<any>;
  private _relativeSize!: number;

  constructor(
    private loadImageService: LoadImageService,
    private testHookService: TestHookService,
    injector: Injector,
  ) {
    super(injector);
    this.sizeEventTracker = new DebouncedEventTracker(
      { id: 'change_superpixel_size', bucket: 'annotation' },
      buildTrackerFromShellConnector(this.shellConnector),
    );
  }

  get relativeSize() {
    return this._relativeSize;
  }

  get selectedAlgorithm() {
    return this._selectedAlgorithm;
  }

  set selectedAlgorithm(value: AbstractAlgorithmAdapter<any>) {
    const start = performance.now();

    this.setAlgorithm(value);

    this.shellConnector.trackEvent({
      id: 'select_superpixel_algorithm',
      bucket: 'annotation',
      algorithm: value.name,
      duration: performance.now() - start,
    });
  }

  override async onActivate() {
    const imageData = await cacheImageSourcePixels(
      this.loadImageService,
      this.store,
    );

    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (this.result) {
      this.redrawBoundaryMask();
    } else {
      this.algorithms = [
        new SLICAdapter(imageData),
        new SLICOAdapter(imageData),
        new PFFAdapter(imageData),
      ];
      this.setAlgorithm(this.algorithms[0]);
    }

    this.onEvent('container', 'mousedown', this.startPainting);
    this.onEvent('container', 'mousedown', this.startPainting, {
      modifiers: ['altKey'],
    });
    await super.onActivate();
  }

  override onDeactivate() {
    super.onDeactivate();
    this.store('imageOverlayData').set(
      newDataForImage(this.store.state().sensorRendererState),
    );
  }

  finer() {
    this.setSize(this._relativeSize - 1);
  }

  coarser() {
    this.setSize(this._relativeSize + 1);
  }

  setSize(size: number) {
    // fix me if you change this code
    // eslint-disable-next-line no-param-reassign -- fix me!
    size = clamp(
      size,
      this.selectedAlgorithm.minRelativeSize,
      this.selectedAlgorithm.maxRelativeSize,
    );
    if (size === this._relativeSize) {
      return;
    }

    this.sizeEventTracker.startEvent();

    this._relativeSize = size;
    this.refreshSuperpixels();

    this.sizeEventTracker.trackWithDebounce({
      size: this._relativeSize,
      algorithm: this.selectedAlgorithm.name,
    });
  }

  getPointsOfSuperpixelAt(point: Point2D) {
    const position = toMaskPosition(
      point,
      this.store.state().sensorRendererState,
    );
    const superpixelId = this.result.superpixelMap[position];
    return this.positionLookup[superpixelId];
  }

  private startPainting(initialEvent: MouseEvent) {
    const dragHandler = new SuperpixelDragHandler(
      this.getCode(initialEvent),
      initialEvent,
      this.injector,
    );

    dragHandler.drag();
    this.registerHelper(dragHandler);
  }

  private setAlgorithm(value: AbstractAlgorithmAdapter<any>) {
    this._selectedAlgorithm = value;
    this._relativeSize = clamp(0, value.minRelativeSize, value.maxRelativeSize);
    this.refreshSuperpixels();
  }

  private refreshSuperpixels() {
    this.computeSuperpixels();
    this.redrawBoundaryMask();
  }

  private computeSuperpixels() {
    this.result = this.selectedAlgorithm.computeSuperpixels(this._relativeSize);
    this.positionLookup = this.generatePositionLookup();

    this.testHookService.run(computeSuperpixelHook);
  }

  private redrawBoundaryMask() {
    this.store('imageOverlayData').set(this.result.boundaryMask);
  }

  private generatePositionLookup() {
    const superpixelCount = this.result.superpixelCount;
    const lookup = times(superpixelCount, () => [] as number[]);
    const superpixelMap = this.result.superpixelMap;
    for (let i = 0; i < superpixelMap.length; ++i) {
      const id = superpixelMap[i];
      lookup[id].push(i);
    }
    return lookup;
  }
}
